import type { SupabaseClient } from '@supabase/supabase-js';
import { fixedValueChoice } from '../shared/fixed-value-choice.js';
import { showToast } from '@vario-connect/frontend-design';

// ============================================================================
// Otto-Lister — Mapping-Editor (Sub-Tab im Detail-Pane).
//
// Verbindet otto-listing-schema (Otto-Zielfelder) × vario-mapping-sources
// (Vario-Quellen, OHNE storefront → nur generische article/listing/variant-Quellen)
// und persistiert pro otto_category ein Profil via otto_save_mapping_profile.
// Brand wird NICHT hier gemappt (per-Listing im Übersicht-Tab gesetzt).
// ============================================================================

export interface MappingTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}

export interface MappingTabArgs {
  ottoCategory: string | null;
  varioArticleId: string;
}

export interface SchemaField {
  key: string; group: string; label: string; required: boolean; dataType: string;
  // Nur Kategorie-Attribute (otto-listing-schema):
  relevance?: string; recommended?: boolean; allowedValues?: string[]; exampleValues?: string[];
  unit?: string; multiValue?: boolean; isVariationTheme?: boolean; description?: string;
  attributeGroup?: string;
  defaultVarioPath?: string;
}
export interface SourceDef { sourceKey: string; path: string; dataType: string; label: string; isVariantAttribute?: boolean }
export interface SourceGroup { key: string; label: string; sources: SourceDef[] }
export interface ProfileField {
  otto_field_key: string; source_type: string;
  vario_path: string | null; fixed_value: unknown; template: string | null;
  field_group?: string;
}

const SOURCE_TYPES: Array<{ value: string; label: string }> = [
  { value: 'unmapped', label: 'Nicht gemappt' },
  { value: 'vario_path', label: 'Vario-Feld' },
  { value: 'fixed_value', label: 'Fester Wert' },
  { value: 'template', label: 'Vorlage {feld}' },
];

export async function renderMappingTab(host: HTMLElement, ctx: MappingTabCtx, args: MappingTabArgs): Promise<void> {
  if (!args.ottoCategory) {
    host.innerHTML = banner('warn', 'Für dieses Listing ist keine OTTO-Kategorie gesetzt — Mapping ist kategoriegebunden.');
    return;
  }
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Mapping…</div>`;

  let schemaFields: SchemaField[];
  let sourceGroups: SourceGroup[];
  let existing: Map<string, ProfileField>;
  try {
    const [schema, sources, profile] = await Promise.all([
      callFn<{ fields: SchemaField[]; refreshPending?: boolean }>(ctx, 'otto-listing-schema', { ottoCategory: args.ottoCategory }),
      callFn<{ groups: SourceGroup[] }>(ctx, 'vario-mapping-sources', { action: 'list', articleId: args.varioArticleId }),
      ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: args.ottoCategory }),
    ]);
    // Mapping-Tab: Produktdaten-Basis + Varianten-/Pflichtfelder (SKU/EAN/Preis) + die
    // PFLICHT-Kategorie-Attribute (required = featureRelevance LEGAL) in eigener Sektion. Die
    // restlichen (empfohlenen/optionalen) Kategorie-Attribute bleiben im Reiter „Optionale
    // Felder". 'brand' (Übersicht) und 'media' (Bilder-Tab) gehören NICHT hierher.
    schemaFields = (schema.fields ?? []).filter(
      (f) => f.key !== 'brand' && (
        f.group === 'product_data_basic'
        || f.group === 'variants'
        || (f.group === 'category_optional_attributes' && f.required)
      ),
    );
    sourceGroups = sources.groups ?? [];
    const prof = (profile.data ?? null) as { fields?: ProfileField[] } | null;
    existing = new Map((prof?.fields ?? []).map((f) => [f.otto_field_key, f]));
  } catch (e) {
    host.innerHTML = banner('error', `Mapping nicht ladbar: ${(e as Error).message}`);
    return;
  }

  // Otto-seitige Label-Klarstellungen für generische Vario-Quellen (kein Eingriff in die
  // Function vario-mapping-sources, die Labels werden nur hier im Frontend überschrieben).
  const LABEL_OVERRIDES: Record<string, string> = {
    'listing.ean': 'EAN (Verkaufskanal, sonst Stammdaten)',
  };

  // Flache Quellenliste (für die vario_path-Auswahl) — Varianten-Attribute hier ausgeblendet
  // (die gehören in den Varianten-Tab).
  const flatSources = sourceGroups.flatMap((g) =>
    g.sources
      .filter((s) => !s.isVariantAttribute)
      .map((s) => ({ group: g.label, ...s, label: LABEL_OVERRIDES[s.sourceKey] ?? s.label })));

  // Meta-Strip: Kategorie + Marke als kompakte Pills. Die „Ändern"-Buttons delegieren auf die
  // bereits im Detail-Pane verdrahteten Picker-Buttons (#otto-cat-pick-detail / #otto-brand-pick) —
  // diese tragen die volle Logik (Kategorie-Cascade an Kanal-Kinder, Brand-Persist, Pane-Re-Render).
  // So bleibt diese Datei reiner Reskin, ohne den MappingTabCtx-Contract aufzubohren.
  host.innerHTML = `
    <div class="v-pa-md">
      <div class="otto-meta-strip">
        <span class="kl-meta-pill">
          <span class="kl-mp-ic v-icon fa-regular fa-table-cells-large"></span>
          <span class="kl-mp-lab">Kategorie</span>
          <span class="kl-mp-val" id="otto-meta-cat-val">${escapeHtml(args.ottoCategory)}</span>
          <button type="button" class="kl-mp-edit" id="otto-meta-cat-edit">Ändern</button>
        </span>
        <span class="kl-meta-pill">
          <span class="kl-mp-ic v-icon fa-regular fa-tag"></span>
          <span class="kl-mp-lab">Marke</span>
          <span class="kl-mp-val" id="otto-meta-brand-val"><span class="v-text-muted">—</span></span>
          <button type="button" class="kl-mp-edit" id="otto-meta-brand-edit">Ändern</button>
        </span>
      </div>
      <div id="otto-map-fields"></div>
    </div>`;

  wireMetaStrip(host, args);

  const fieldsHost = host.querySelector<HTMLDivElement>('#otto-map-fields')!;
  const basis = schemaFields.filter((f) => f.group === 'product_data_basic');
  const variants = schemaFields.filter((f) => f.group === 'variants');
  // Pflicht-Kategorie-Attribute (LEGAL) — eigene Sektion, alphabetisch + Pflicht zuerst.
  const requiredAttrs = schemaFields
    .filter((f) => f.group === 'category_optional_attributes')
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  const renderSection = (title: string, sub: string, list: SchemaField[]): string =>
    `<div class="kl-sec-lab">${escapeHtml(title)}</div>`
    + (sub ? `<div class="kl-sec-note">${sub}</div>` : '')
    + (list.length
        ? list.map((f) => renderFieldRow(f, existing.get(f.key), flatSources)).join('')
        : `<div class="v-text-muted" style="font-size:12px;padding:6px 0;">Keine Felder.</div>`);
  fieldsHost.innerHTML =
    renderSection(`Mapping · ${args.ottoCategory}`, '', basis)
    + renderSection('Varianten-Pflichtfelder', 'Pro Variation (SKU). SKU & EAN sind Pflicht; Preis optional im Lister.', variants)
    + (requiredAttrs.length
        ? renderSection(
            `Kategorie-Pflichtfelder · ${args.ottoCategory}`,
            'Von Otto für diese Kategorie rechtlich/zwingend verlangt. Weitere (empfohlene/optionale) Attribute im Reiter „Optionale Felder".',
            requiredAttrs)
        : '');

  // Pro Zeile: Wertcontrol je nach source_type ein-/ausblenden.
  fieldsHost.querySelectorAll<HTMLSelectElement>('[data-st-select]').forEach((sel) => {
    const sync = () => syncRowControls(fieldsHost, sel.dataset['stSelect']!, sel.value);
    sel.addEventListener('change', sync);
    sync();
  });

  // Speichern-Logik (liest den DOM-State erst zur Ausführungszeit) — wird über die zentrale
  // Save-Registry des Detail-Panes vom globalen „Speichern"-Button ausgeführt (kein eigener Button).
  const save = async (): Promise<void> => {
    const ownFields = collectFields(fieldsHost, schemaFields);
    // otto_save_mapping_profile ersetzt ALLES. ownFields enthält jetzt Produktdaten + Varianten
    // + die PFLICHT-Kategorie-Attribute. Erhalten bleiben müssen die NICHT-Pflicht-Kategorie-
    // Attribute (Reiter „Optionale Felder") + Varianten — sonst würde Speichern sie löschen.
    const requiredKeys = new Set(
      schemaFields.filter((f) => f.group === 'category_optional_attributes').map((f) => f.key),
    );
    const cur = await loadMappingProfile(ctx, args.ottoCategory!);
    const preserved = cur.fields
      .filter((f) => f.field_group === 'category_optional_attributes' && !requiredKeys.has(f.otto_field_key))
      .map(toFieldSaveShape);
    const variants = cur.variants.map(toVariantSaveShape);
    const { error } = await ctx.supabase.rpc('otto_save_mapping_profile', {
      _otto_category: args.ottoCategory, _fields: [...ownFields, ...preserved], _variants: variants,
    });
    if (error) throw new Error(error.message);
  };
  ctx.registerSaver?.('mapping', save);
}

/**
 * Meta-Strip-Verdrahtung. Der Mapping-Tab rendert nur die Pills; die „Ändern"-Buttons
 * delegieren auf die bereits im Detail-Pane verdrahteten Picker (#otto-cat-pick-detail /
 * #otto-brand-pick), die die volle Logik tragen (Kategorie-Cascade an Kanal-Kinder via
 * otto-assign-listing-category + otto_set_listing_category, Brand-Persist via
 * otto_set_listing_brand, anschließendes Pane-Re-Render). Findet das Pane diese Buttons
 * nicht (z. B. Mapping-Tab außerhalb des Detail-Panes gerendert), bleiben die Pills reine
 * Anzeige und die „Ändern"-Buttons werden ausgeblendet.
 */
function wireMetaStrip(host: HTMLElement, args: MappingTabArgs): void {
  const root = host.closest<HTMLElement>('.otto-lv2') ?? document;
  const catBtn = root.querySelector<HTMLButtonElement>('#otto-cat-pick-detail');
  const brandBtn = root.querySelector<HTMLButtonElement>('#otto-brand-pick');
  const brandCurrent = root.querySelector<HTMLElement>('#otto-brand-current');

  const catEdit = host.querySelector<HTMLButtonElement>('#otto-meta-cat-edit');
  const brandEdit = host.querySelector<HTMLButtonElement>('#otto-meta-brand-edit');
  const brandVal = host.querySelector<HTMLElement>('#otto-meta-brand-val');

  // Marken-Wert aus dem (vom Detail-Pane gepflegten) Anzeige-Element spiegeln. Das Detail-Pane
  // löst die aktuelle Marke asynchron auf → kurz nachpollen, bis der Name steht.
  if (brandVal && brandCurrent) {
    const sync = () => { brandVal.innerHTML = brandCurrent.innerHTML; };
    sync();
    let tries = 0;
    const t = setInterval(() => { sync(); if (++tries >= 20) clearInterval(t); }, 250);
  }

  // Kategorie ist im Detail-Pane wählbar (Cascade etc.). Klick auf „Ändern" öffnet denselben
  // Picker; nach erfolgreicher Auswahl rendert das Pane komplett neu (inkl. dieses Tabs).
  if (catEdit && catBtn) {
    catEdit.addEventListener('click', () => catBtn.click());
  } else if (catEdit) {
    catEdit.hidden = true;
  }
  if (brandEdit && brandBtn) {
    brandEdit.addEventListener('click', () => brandBtn.click());
  } else if (brandEdit) {
    brandEdit.hidden = true;
  }

  // Die alten Kategorie/Marke-Tabellenzeilen im Detail-Pane sind durch den Meta-Strip ersetzt →
  // ausblenden (nur, wenn die Buttons im selben Pane gefunden wurden; sonst nichts anfassen).
  if (catBtn) {
    const legacyTable = catBtn.closest<HTMLElement>('table.klst-detail-table');
    if (legacyTable) legacyTable.style.display = 'none';
  }
  void args;
}

// ── Geteilt mit dem „Optionale Felder"-Tab (otto-list-optional-fields.ts) ────

export interface LoadedProfile { fields: ProfileField[]; variants: unknown[] }

export async function loadMappingProfile(ctx: MappingTabCtx, ottoCategory: string): Promise<LoadedProfile> {
  const { data } = await ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: ottoCategory });
  const p = (data ?? null) as { fields?: ProfileField[]; variants?: unknown[] } | null;
  return { fields: p?.fields ?? [], variants: p?.variants ?? [] };
}

/** Profil-Feld → Save-Shape für otto_save_mapping_profile._fields (unverändert durchreichen). */
export function toFieldSaveShape(f: ProfileField): Record<string, unknown> {
  return {
    otto_field_key: f.otto_field_key,
    field_group: f.field_group ?? 'category_optional_attributes',
    source_type: f.source_type,
    vario_path: f.vario_path,
    fixed_value: f.fixed_value,
    template: f.template,
  };
}

export function renderFieldRow(f: SchemaField, mapping: ProfileField | undefined, sources: Array<{ group: string } & SourceDef>): string {
  // Ohne gespeichertes Mapping: Default-Vorbelegung (z. B. productReference/sku ← Vario-SKU
  // article.number). Sobald der Händler speichert/überschreibt, gewinnt das Profil.
  const st = mapping ? mapping.source_type : (f.defaultVarioPath ? 'vario_path' : 'unmapped');
  const varioPath = mapping ? (mapping.vario_path ?? '') : (f.defaultVarioPath ?? '');
  const fixedVal = typeof mapping?.fixed_value === 'string' ? mapping.fixed_value : (mapping?.fixed_value != null ? String(mapping.fixed_value) : '');
  const template = mapping?.template ?? '';
  const opts = sources.map((s) => `<option value="${escapeAttr(s.path)}"${s.path === varioPath ? ' selected' : ''}>${escapeHtml(s.group)} · ${escapeHtml(s.label)}</option>`).join('');
  // Feste Auswahl nur bei OTTOs allowedValues (fixed-value-choice.ts). Beispiele sind Vorschläge.
  const choice = fixedValueChoice(f);
  const enforceSelect = choice.mode === 'select';
  const listId = `dl-${cssEscape(f.key)}`;
  const datalist = choice.mode === 'suggest'
    ? `<datalist id="${escapeAttr(listId)}">${choice.values.map((v) => `<option value="${escapeAttr(v)}"></option>`).join('')}</datalist>`
    : '';
  const fixedControl = enforceSelect
    ? `<select class="v-input" data-ctl="fixed_value" data-for="${escapeAttr(f.key)}" hidden>
          <option value="">— Wert wählen —</option>
          ${choice.values.map((v) => `<option value="${escapeAttr(v)}"${v === fixedVal ? ' selected' : ''}>${escapeHtml(v)}</option>`).join('')}
        </select>`
    : `<input class="v-input" data-ctl="fixed_value" data-for="${escapeAttr(f.key)}" placeholder="Fester Wert"${datalist ? ` list="${escapeAttr(listId)}"` : ''} value="${escapeAttr(fixedVal)}" hidden />`;
  // Attribute mit festen Otto-Vorgabewerten klar markieren (Badge + Zeilen-Akzent), damit der
  // Händler sofort sieht: hier nur aus der Liste wählen (kein Freitext).
  const valueCount = f.allowedValues?.length ?? 0;
  const hasAllowed = valueCount > 0;
  const vsetBadge = hasAllowed
    ? `<span class="kl-vset"><span class="kl-vset-d"></span>${enforceSelect ? 'feste Werte' : 'feste Werte (Mehrfach)'} · ${valueCount}</span>`
    : '';
  const axisBadge = f.isVariationTheme ? `<span class="kl-vset kl-vset--axis"><span class="kl-vset-d"></span>Varianten-Achse</span>` : '';
  const reqBadge = f.required ? `<span class="kl-mf-req"><span class="kl-mf-req-d"></span>Pflicht</span>` : '';
  const recBadge = f.recommended ? `<span class="kl-mf-rec"><span class="kl-mf-rec-d"></span>empfohlen</span>` : '';
  const titleAttr = f.description ? ` title="${escapeAttr(f.description)}"` : '';
  const rowRec = hasAllowed ? ' otto-mfield--accent' : '';
  return `
    <div class="otto-mfield${rowRec}" data-field="${escapeAttr(f.key)}"${titleAttr}>
      <div class="otto-mfield-label">
        <div class="kl-mf-label">${escapeHtml(f.label)}${reqBadge}${recBadge}</div>
        <div class="kl-mf-code">${escapeHtml(f.key)}</div>
        ${vsetBadge || axisBadge ? `<div class="kl-mf-badges">${vsetBadge}${axisBadge}</div>` : ''}
      </div>
      <select class="v-input kl-sel" data-st-select="${escapeAttr(f.key)}">
        ${SOURCE_TYPES.map((t) => `<option value="${t.value}"${t.value === st ? ' selected' : ''}>${t.label}</option>`).join('')}
      </select>
      <div class="otto-mfield-ctl">
        <select class="v-input kl-sel" data-ctl="vario_path" data-for="${escapeAttr(f.key)}" hidden>
          <option value="">— Vario-Feld wählen —</option>${opts}
        </select>
        ${fixedControl}
        <input class="v-input kl-inp" data-ctl="template" data-for="${escapeAttr(f.key)}" placeholder="z.B. {article.manufacturer} {article.name}" value="${escapeAttr(template)}" hidden />
        ${datalist}
      </div>
    </div>`;
}

export function syncRowControls(host: HTMLElement, fieldKey: string, sourceType: string): void {
  host.querySelectorAll<HTMLElement>(`[data-for="${cssEscape(fieldKey)}"]`).forEach((ctl) => {
    ctl.hidden = ctl.dataset['ctl'] !== sourceType;
  });
}

export function collectFields(host: HTMLElement, schemaFields: SchemaField[]): unknown[] {
  const byKey = new Map(schemaFields.map((f) => [f.key, f.group]));
  const out: unknown[] = [];
  host.querySelectorAll<HTMLSelectElement>('[data-st-select]').forEach((sel) => {
    const key = sel.dataset['stSelect']!;
    const st = sel.value;
    if (st === 'unmapped') return;
    const row = sel.closest('.otto-mfield')!;
    const entry: Record<string, unknown> = { otto_field_key: key, field_group: byKey.get(key) ?? 'product_data_basic', source_type: st };
    if (st === 'vario_path') entry['vario_path'] = (row.querySelector<HTMLSelectElement>('[data-ctl="vario_path"]')?.value ?? '') || null;
    if (st === 'fixed_value') entry['fixed_value'] = row.querySelector<HTMLInputElement>('[data-ctl="fixed_value"]')?.value ?? '';
    if (st === 'template') entry['template'] = row.querySelector<HTMLInputElement>('[data-ctl="template"]')?.value ?? '';
    out.push(entry);
  });
  return out;
}

export function toVariantSaveShape(v: unknown): unknown {
  const o = v as Record<string, unknown>;
  return {
    otto_attribute_name: o['otto_attribute_name'],
    vario_variant_attribute_name: o['vario_variant_attribute_name'],
    is_discriminator: o['is_discriminator'],
    values: o['values'],
  };
}

export async function callFn<T>(ctx: MappingTabCtx, name: string, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`${name} ${res.status}: ${t.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export function banner(kind: 'warn' | 'error', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : 'v-banner-warn';
  const icon = kind === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${escapeHtml(msg)}</div></div></div>`;
}

export function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
export function escapeAttr(s: string): string { return escapeHtml(s); }
function cssEscape(s: string): string { return s.replace(/["\\]/g, '\\$&'); }
